import type { library } from '@cwos/api-client';

/**
 * Pure view logic for the Organizational Library. No DOM, no react-query, no
 * `@cwos/ui-admin` — so every judgement below is testable in the node tier.
 *
 * ===========================================================================
 * THE ONE RULE THIS FILE EXISTS TO ENFORCE
 * ===========================================================================
 * NEVER EXPOSE GRAPH INTERNALS JUST BECAUSE THEY EXIST. Every value that
 * crosses from the API into a Library screen goes through a function here and
 * comes out in operator language.
 *
 * `bindingness: 'strong_default'` is a column in a CHECK constraint. What an
 * agency director needs to read is "Expected unless there's a documented
 * reason". `rule_level: 'client_specific'` is a rank in a hierarchy; what they
 * need is "Set for one person". `state: 'superseded'` is a lifecycle enum;
 * what they need is "Replaced". A screen that printed the enum would be
 * showing them the schema and calling it a product.
 *
 * The translations are TOTAL FUNCTIONS over the closed sets the storage layer
 * fixes. Each has a `default:` that returns the raw value rather than an empty
 * string, because a word added to a CHECK constraint by a future migration
 * should read as an unfamiliar word, never as a blank — a blank is
 * indistinguishable from "this rule has no bindingness", which is a state that
 * cannot exist.
 */

// ---------------------------------------------------------------------------
// Bindingness — how much this rule binds
// ---------------------------------------------------------------------------

/**
 * The severity a bindingness reads as, for `<Pill variant>`.
 *
 * A hard constraint is not an ALARM — nothing is wrong — but it is the strongest
 * thing on the screen and must not read the same as advisory text. "The statute
 * requires" and "the manual suggests" looking alike is the specific failure this
 * mapping prevents.
 */
export type PillVariant = 'good' | 'look' | 'urgent' | 'unsure' | 'plain' | 'sky';

export function bindingnessVariant(b: library.Bindingness | string): PillVariant {
  switch (b) {
    case 'hard_constraint':
      return 'urgent';
    case 'strong_default':
      return 'look';
    case 'guidance':
      return 'sky';
    case 'informational':
      return 'plain';
    default:
      // An unrecognised level reads as plain rather than as anything with a
      // colour: guessing a severity for a word we do not know would be the
      // screen inventing an authority claim.
      return 'plain';
  }
}

export function bindingnessLabel(b: library.Bindingness | string): string {
  switch (b) {
    case 'hard_constraint':
      return 'Required';
    case 'strong_default':
      return 'Expected';
    case 'guidance':
      return 'Guidance';
    case 'informational':
      return 'For reference';
    default:
      return b;
  }
}

/** The sentence under the pill. What the word MEANS for someone deciding what to do. */
export function bindingnessSentence(b: library.Bindingness | string): string {
  switch (b) {
    case 'hard_constraint':
      return 'This cannot be waived locally. Anything your organization sets must be at least this strict.';
    case 'strong_default':
      return 'This is what your organization expects unless somebody documents a reason to depart from it.';
    case 'guidance':
      return 'Advice to weigh, not a requirement. It does not override anything.';
    case 'informational':
      return 'Background. It informs a decision and does not decide one.';
    default:
      return 'Phibian does not recognise this level and is showing it as stored.';
  }
}

// ---------------------------------------------------------------------------
// Rule level — how specific this rule is
// ---------------------------------------------------------------------------

export function ruleLevelLabel(level: library.RuleLevel | string): string {
  switch (level) {
    case 'external_requirement':
      return 'Outside requirement';
    case 'organization':
      return 'Whole organization';
    case 'program':
      return 'One program';
    case 'service_type':
      return 'One service';
    case 'location':
      return 'One site';
    case 'client_specific':
      return 'One person';
    default:
      return level;
  }
}

/**
 * Which rule wins, in a sentence.
 *
 * The hierarchy is genuinely counter-intuitive in one place and the screen has
 * to say so: among the organization's own policies MORE SPECIFIC WINS, but an
 * outside requirement does not compete on specificity at all — it is a floor
 * that the most specific internal policy is still measured against.
 */
export function ruleLevelSentence(level: library.RuleLevel | string): string {
  switch (level) {
    case 'external_requirement':
      return 'Set outside your organization. It is a floor: your own policy can be stricter, never looser.';
    case 'organization':
      return 'Applies everywhere, unless a narrower policy answers the same question.';
    case 'program':
      return 'Applies inside this program, and beats the organization-wide answer here.';
    case 'service_type':
      return 'Applies to this service, and beats the program answer here.';
    case 'location':
      return 'Applies at this site, and beats the service answer here.';
    case 'client_specific':
      return 'Set for one person. It beats every broader policy for them.';
    default:
      return 'Phibian does not recognise this level and is showing it as stored.';
  }
}

// ---------------------------------------------------------------------------
// Lifecycle states
// ---------------------------------------------------------------------------

export function ruleStateLabel(state: library.RuleState | string): string {
  switch (state) {
    case 'active':
      return 'In force';
    case 'superseded':
      return 'Replaced';
    case 'retired':
      return 'Withdrawn';
    default:
      return state;
  }
}

export function ruleStateVariant(state: library.RuleState | string): PillVariant {
  switch (state) {
    case 'active':
      return 'good';
    case 'superseded':
    case 'retired':
      return 'plain';
    default:
      return 'plain';
  }
}

export function ingestionStateLabel(state: library.IngestionState | string): string {
  switch (state) {
    case 'source_available':
      return 'Current';
    case 'superseded':
      return 'Replaced';
    case 'retired':
      return 'Withdrawn';
    default:
      return state;
  }
}

/**
 * What became of a proposed reading.
 *
 * `extraction_proposed` and `awaiting_approval` differ only in whether a review
 * task was raised, which is Phibian's own bookkeeping and not a distinction a
 * reviewer acts on. Both read "Awaiting review".
 */
export function extractionStateLabel(state: library.ExtractionState | string): string {
  switch (state) {
    case 'extraction_proposed':
    case 'awaiting_approval':
      return 'Awaiting review';
    case 'approved_operational_knowledge':
      return 'Approved';
    case 'rejected':
      return 'Not accepted';
    case 'superseded':
      return 'Replaced';
    case 'retired':
      return 'Withdrawn';
    default:
      return state;
  }
}

export function extractionKindLabel(kind: library.ExtractionKind | string): string {
  switch (kind) {
    case 'definition':
      return 'A definition';
    case 'rule':
      return 'A rule';
    case 'responsibility':
      return 'A responsibility';
    case 'workflow':
      return 'A workflow';
    default:
      return kind;
  }
}

// ---------------------------------------------------------------------------
// Applicability — the part most likely to be rendered as a graph dump
// ---------------------------------------------------------------------------

/** One narrowing dimension, ready to render. */
export interface Narrowing {
  readonly dimension: string;
  readonly count: number;
}

/**
 * WHICH DIMENSIONS THIS RULE WAS NARROWED ON — never the identifiers.
 *
 * The arrays hold UUIDs. Printing them would be showing an operator the
 * primary keys of their own database and calling it applicability; and worse,
 * `clientIds` holds PARTICIPANT identifiers, which is a disclosure a rule
 * listing has no business making. So this returns dimensions and counts, and
 * the screen resolves names only where it independently may.
 *
 * AN EMPTY ARRAY IS NOT A NARROWING AND MUST NOT BE LISTED. `programIds: []`
 * means "this rule is not narrowed by program", which is the WIDEST possible
 * answer — rendering it as "Programs: 0" would say the exact opposite of what
 * it means. {@link appliesEverywhere} is the sentence for the all-empty case.
 */
export function narrowings(rule: {
  jurisdictions: readonly string[];
  programIds: readonly string[];
  serviceIds: readonly string[];
  locationIds: readonly string[];
  payers: readonly string[];
  roleIds: readonly string[];
  clientIds: readonly string[];
}): Narrowing[] {
  const all: Narrowing[] = [
    { dimension: 'State or region', count: rule.jurisdictions.length },
    { dimension: 'Programs', count: rule.programIds.length },
    { dimension: 'Services', count: rule.serviceIds.length },
    { dimension: 'Sites', count: rule.locationIds.length },
    { dimension: 'Payers', count: rule.payers.length },
    { dimension: 'Roles', count: rule.roleIds.length },
    { dimension: 'People', count: rule.clientIds.length },
  ];
  return all.filter((n) => n.count > 0);
}

export function appliesEverywhere(rule: Parameters<typeof narrowings>[0]): boolean {
  return narrowings(rule).length === 0;
}

/** The one-line answer to "who does this apply to?". */
export function applicabilitySentence(rule: Parameters<typeof narrowings>[0]): string {
  const narrowed = narrowings(rule);
  if (narrowed.length === 0) {
    return 'Everyone in the organization. This rule is not narrowed to any program, service, site, payer, role or person.';
  }
  return `Narrowed by ${narrowed.map((n) => n.dimension.toLowerCase()).join(', ')}.`;
}

// ---------------------------------------------------------------------------
// Dates
// ---------------------------------------------------------------------------

/**
 * The window a rule covered, in words.
 *
 * `effectiveTo: null` on an ACTIVE rule means still in force. On a SUPERSEDED
 * rule it would mean a rule that stopped being current and never stopped
 * applying, which is the exact defect this lane closed at the storage layer —
 * so it is called out here rather than rendered as an open range, because a
 * screen showing "From 1 June — ongoing" beside "Replaced" is showing an
 * operator two contradictory facts and asking them to pick.
 */
export function effectiveWindow(rule: {
  effectiveFrom: string;
  effectiveTo: string | null;
  state: library.RuleState | string;
}): string {
  const from = `From ${formatDay(rule.effectiveFrom)}`;
  if (rule.effectiveTo) return `${from} to ${formatDay(rule.effectiveTo)}`;
  if (rule.state === 'active') return `${from}, still in force`;
  return `${from}. Phibian has no end date for this rule even though it is no longer current.`;
}

/**
 * A `YYYY-MM-DD` calendar day, rendered without a timezone.
 *
 * `new Date('2026-06-01')` parses as UTC midnight and then renders in the
 * viewer's zone, which west of Greenwich prints 31 May. These are `date`
 * columns — a day, not an instant — so the parts are split by hand and no
 * `Date` is constructed at all.
 */
export function formatDay(iso: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!match) return iso;
  const [, y, m, d] = match;
  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];
  const monthName = months[Number(m) - 1];
  if (!monthName) return iso;
  return `${Number(d)} ${monthName} ${y}`;
}

/** An ISO instant as a day. Used for `approvedAt` / `retrievedAt`, where the day is the fact. */
export function formatInstantDay(iso: string): string {
  return formatDay(iso.slice(0, 10));
}

// ---------------------------------------------------------------------------
// The overview's four states
// ---------------------------------------------------------------------------

/**
 * What the review-queue tile says.
 *
 * The whole reason this is a function and not `String(pendingApprovals ?? 0)`:
 *
 *   REFUSED  the caller does not hold the reviewing permission. The count was
 *            never asked for. Showing `0` would tell a supervisor their queue
 *            is clear when in fact they cannot see it.
 *   ZERO     a reviewer whose queue really is empty. A measured zero.
 *   COUNT    a reviewer with work waiting.
 *
 * The three are different sentences on the screen, and `kind` is what the
 * component switches on so a fourth state cannot be added silently.
 */
export type PendingTile =
  | { kind: 'refused'; label: string; detail: string }
  | { kind: 'zero'; label: string; detail: string }
  | { kind: 'count'; label: string; detail: string; value: number };

export function pendingTile(overview: {
  pendingApprovals: number | null;
  pendingApprovalsRefused: boolean;
}): PendingTile {
  if (overview.pendingApprovalsRefused) {
    return {
      kind: 'refused',
      label: 'Not shown to you',
      detail:
        'Reviewing proposed knowledge needs a permission you do not hold, so Phibian has not ' +
        'looked. This is not a count of zero.',
    };
  }
  const value = overview.pendingApprovals ?? 0;
  if (value === 0) {
    return {
      kind: 'zero',
      label: 'Nothing waiting',
      detail: 'Every reading taken from your documents has been decided.',
    };
  }
  return {
    kind: 'count',
    label: value === 1 ? '1 waiting for you' : `${value} waiting for you`,
    detail: 'Proposed readings nobody has approved or turned down yet. Nothing here is in force.',
    value,
  };
}

/**
 * The sentence under the headline figures when the reader is narrowed.
 *
 * `null` when they are not — a screen that always printed a caveat would train
 * people to ignore it, which costs more than the caveat buys.
 */
export function scopeCaveat(overview: { countsAreOrganizationWide: boolean }): string | null {
  if (overview.countsAreOrganizationWide) return null;
  return (
    'These figures cover the documents in your own programs — not the whole organization. ' +
    'Someone with organization-wide knowledge access will see more.'
  );
}

// ---------------------------------------------------------------------------
// What depends on a rule
// ---------------------------------------------------------------------------

/**
 * WHAT THE DEPENDENTS PANEL SAYS, AS FOUR SENTENCES THAT CANNOT BE WORDED INTO
 * EACH OTHER.
 *
 * This screen used to answer "what relies on this" with a single honest
 * absence, because nothing in the product recorded that a rule had been used.
 * Founder ruling 4's migration changed that for ONE consumer: a recommendation
 * files a doctrine snapshot naming the canonical rule VERSION in force when the
 * advice was given. So there is now a real answer, and a real answer brings
 * back every way of getting it wrong:
 *
 *   REFUSED   the reader may not ask. Phibian did not look, so it has no
 *             number — and a `0` here would tell a compliance lead that nothing
 *             rests on a rule they simply cannot see the dependents of.
 *   NONE      we looked and nothing cites it, and nothing was withheld either.
 *             A measured zero, for recommendations only.
 *   WITHHELD  we looked, things DO cite it, and this reader may see none of
 *             them. "Three decisions rest on this and you may not see them" is
 *             a different fact from "nothing rests on this", and it is usually
 *             the more important one: it is the sentence that tells somebody to
 *             go and find a colleague who can look.
 *   SOME      a list, possibly with some of it withheld as well.
 *
 * `kind` is what the component switches on, so a fifth state cannot appear by
 * somebody adding a branch to a ternary.
 */
export type DependentsSummary =
  | { kind: 'refused'; label: string; detail: string }
  | { kind: 'none'; label: string; detail: string }
  | { kind: 'withheld'; label: string; detail: string; withheld: number }
  | { kind: 'some'; label: string; detail: string; shown: number; withheld: number };

/** Which question a facet answers. The two have genuinely different answers. */
export type DependentsScope = 'version' | 'policy';

const SUBJECT: Record<DependentsScope, string> = {
  version: 'this version of the rule',
  policy: 'any version of this rule',
};

function decisions(n: number): string {
  return n === 1 ? '1 decision' : `${n} decisions`;
}

/**
 * The facet, in words.
 *
 * The `state`/`withheld`/`dependents.length` triple comes off the wire exactly
 * as the server computed it, and this function is the ONLY place it is turned
 * into a sentence — so "we did not look" can never be rendered by the branch
 * that means "we looked and found nothing".
 */
export function dependentsSummary(
  facet:
    | { state: 'refused_for_scope'; permission: string }
    | { state: 'tracked'; dependents: readonly unknown[]; withheld: number },
  scope: DependentsScope,
): DependentsSummary {
  if (facet.state === 'refused_for_scope') {
    return {
      kind: 'refused',
      label: 'Not shown to you',
      detail:
        `Seeing which decisions rest on ${SUBJECT[scope]} needs the ${facet.permission} ` +
        'permission, which you do not hold organization-wide. Phibian has not looked, so it ' +
        'has no number to give you. This is not a count of zero — someone with wider access ' +
        'can tell you.',
    };
  }
  const shown = facet.dependents.length;
  const withheld = facet.withheld;
  if (shown === 0 && withheld === 0) {
    return {
      kind: 'none',
      label: 'Nothing recorded',
      detail:
        `No recommendation Phibian has filed cites ${SUBJECT[scope]}. That is a measured zero ` +
        'for recommendations, and only for recommendations — see below.',
    };
  }
  if (shown === 0) {
    return {
      kind: 'withheld',
      label: `${decisions(withheld)}, none shown to you`,
      detail:
        `${decisions(withheld)} rest on ${SUBJECT[scope]}, and you may see none of them: each ` +
        'was built from records outside your reach. This is not zero. Someone who can read ' +
        'those records can tell you what they are.',
      withheld,
    };
  }
  return {
    kind: 'some',
    label: shown === 1 ? '1 decision rests on this' : `${shown} decisions rest on this`,
    detail:
      withheld === 0
        ? `Every recommendation Phibian has filed that cites ${SUBJECT[scope]} is listed here.`
        : `${decisions(withheld)} more rest on ${SUBJECT[scope]} and are not shown to you: they ` +
          'were built from records outside your reach.',
    shown,
    withheld,
  };
}

/**
 * The sentence about the OTHER versions, or `null` when there are none.
 *
 * A rule that has never been revised has one version, and for it the two
 * questions are the same question — printing both answers would invite a reader
 * to believe they had learned two things.
 */
export function lineageCaveat(lineageVersions: number): string | null {
  if (lineageVersions <= 1) return null;
  return (
    `This rule has ${lineageVersions} versions. A decision cites the version that was in force ` +
    'the day it was made, so the two answers below are different questions: what rests on the ' +
    'version you are looking at, and what rests on the rule across all of its versions.'
  );
}

// ---------------------------------------------------------------------------
// CLASSIFY — what kind of document this is, and which topic a reading is
// filed under. Deterministic; a person confirms or changes every suggestion.
// ---------------------------------------------------------------------------

/**
 * Words Ask Phibian drops before it folds a question into topics. A MIRROR of
 * `apps/api/src/domains/intelligence/doctrineProduction.ts:TOPIC_STOPWORDS`,
 * held equal by `library-subject-key-parity.test.ts` — if the two drift, a
 * topic filed here stops being findable there.
 */
export const ASK_STOPWORDS: ReadonlySet<string> = new Set([
  'and',
  'are',
  'but',
  'can',
  'did',
  'does',
  'for',
  'has',
  'have',
  'how',
  'the',
  'was',
  'were',
  'what',
  'when',
  'where',
  'which',
  'who',
  'why',
  'you',
  'your',
  'our',
  'their',
  'this',
  'that',
  'these',
  'those',
  'with',
  'from',
  'into',
  'about',
  'should',
  'would',
  'could',
  'need',
  'needs',
  'please',
  'tell',
  'show',
  'give',
]);

/** Common policy filler: never a topic SUGGESTION (a person may still type one). */
const NOT_A_TOPIC: ReadonlySet<string> = new Set([
  'all',
  'any',
  'also',
  'been',
  'each',
  'its',
  'may',
  'must',
  'not',
  'only',
  'other',
  'shall',
  'such',
  'than',
  'then',
  'them',
  'they',
  'will',
  'within',
  'per',
  'via',
  'who',
  'one',
  'two',
]);

/** A question's significant words, exactly as `doctrineTopicCandidates` keeps them. */
function askWords(text: string): string[] {
  return text
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((w) => w.length > 2 && !ASK_STOPWORDS.has(w));
}

/**
 * MIRROR of `apps/api/src/domains/knowledge/doctrine/subjects.ts:normalizeSubjectCandidate`
 * (parity-tested): fold text onto the subject-key grammar, or `null`.
 */
export function normalizeSubjectKey(text: string): string | null {
  const folded = text
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[\s\-/\\]+/g, '_')
    .replace(/[^a-z0-9._]/g, '')
    .replace(/_+/g, '_')
    .replace(/\.+/g, '.')
    .replace(/^[._]+|[._]+$/g, '')
    .slice(0, 200);
  return folded.length > 0 && /^[a-z0-9]+(_[a-z0-9]+)*(\.[a-z0-9]+(_[a-z0-9]+)*)*$/.test(folded)
    ? folded
    : null;
}

/**
 * A topic a person typed → the subject key the reading is filed under.
 *
 * Ask Phibian finds a rule when a question contains one whole part of its key,
 * and it folds questions into single words and two-word pairs. So each part
 * (split on "." or ">") keeps its first two significant words: "Lone worker >
 * first home visit" → `lone_worker.first_home`. `askPhrasesFor` shows the person
 * which phrases will find it, so the trimming is never silent.
 */
export function topicSubjectKey(topic: string): string | null {
  const parts = topic
    .split(/[.>]/)
    .map((part) => askWords(part).slice(0, 2).join('_'))
    .filter((part) => part !== '');
  return parts.length === 0 ? null : normalizeSubjectKey(parts.join('.'));
}

/**
 * MIRROR of `subjects.ts:GENERIC_SEGMENTS` (parity-tested): words too common to
 * find a rule by one part of its key. A key made only of one such word is still
 * found by it.
 */
export const GENERIC_SEGMENTS: ReadonlySet<string> = new Set([
  'policy',
  'policies',
  'procedure',
  'procedures',
  'rule',
  'rules',
  'guidance',
  'general',
  'requirement',
  'requirements',
  'client',
  'clients',
  'staff',
  'worker',
  'workers',
  'employee',
  'employees',
  'service',
  'services',
  'program',
  'programs',
  'organization',
  'agency',
]);

/**
 * The phrases a question can contain for Ask Phibian to find a rule filed under
 * `key`. Empty when every part is a generic word: Ask could never reach it.
 */
export function askPhrasesFor(key: string): string[] {
  const parts = key.split('.');
  return (parts.length === 1 ? parts : parts.filter((part) => !GENERIC_SEGMENTS.has(part))).map(
    (part) => part.replace(/_/g, ' '),
  );
}

/**
 * Topic suggestions for a passage: its most repeated phrases, a two-word phrase
 * before a single word at the same count, ties broken by first appearance. Deterministic, local,
 * and only ever a suggestion.
 */
export function suggestSubjectKeys(passage: string, limit = 4): string[] {
  const words = askWords(passage);
  const tally = new Map<string, { count: number; first: number; pair: boolean }>();
  const bump = (key: string, at: number, pair: boolean) => {
    const seen = tally.get(key);
    if (seen) seen.count += 1;
    else tally.set(key, { count: 1, first: at, pair });
  };
  words.forEach((w, i) => {
    if (NOT_A_TOPIC.has(w) || /^\d+$/.test(w)) return;
    bump(w, i, false);
    const next = words[i + 1];
    if (next !== undefined && !NOT_A_TOPIC.has(next) && !/^\d+$/.test(next)) {
      bump(`${w}_${next}`, i, true);
    }
  });
  return [...tally.entries()]
    .sort(
      ([, a], [, b]) => b.count - a.count || Number(b.pair) - Number(a.pair) || a.first - b.first,
    )
    .map(([key]) => normalizeSubjectKey(key))
    .filter((key): key is string => key !== null)
    .slice(0, limit);
}

/** The kinds a document can be filed as. Offered as suggestions; a person may type their own. */
export const DOCUMENT_KINDS: readonly string[] = [
  'Employee handbook',
  'Policy',
  'Procedure',
  'Program manual',
  'Contract or agreement',
  'Regulation or statute',
  'Training material',
  'Form or template',
  'Notice or memo',
  'Organizational guidance',
];

const KIND_SIGNALS: readonly (readonly [string, RegExp])[] = [
  ['Employee handbook', /\bhandbook\b/],
  ['Procedure', /\b(procedures?|sop|standard operating)\b/],
  ['Program manual', /\bmanual\b/],
  ['Contract or agreement', /\b(contract|agreement|memorandum of understanding)\b/],
  ['Regulation or statute', /\b(statutes?|regulations?|cfr|administrative code)\b/],
  ['Training material', /\btraining\b/],
  ['Form or template', /\b(form|template)\b/],
  ['Notice or memo', /\b(memo|memorandum|notice|bulletin)\b/],
  ['Organizational guidance', /\b(guidance|guidelines?|best practices?)\b/],
  ['Policy', /\bpolic(y|ies)\b/],
];

/**
 * A suggested kind for a document: the title decides when it says, otherwise the
 * opening of the text; `null` when nothing points anywhere ("we don't know" is
 * a real answer and is shown as one).
 */
export function suggestDocumentKind(title: string, text: string): string | null {
  for (const haystack of [title.toLowerCase(), text.slice(0, 2000).toLowerCase()]) {
    const hit = KIND_SIGNALS.find(([, re]) => re.test(haystack));
    if (hit) return hit[0];
  }
  return null;
}
